import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import {
  Snackbar,
  Alert,
  type AlertColor,
  type SnackbarCloseReason,
} from "@mui/material";

interface NotificationContextType {
  notify: (message: string, severity?: AlertColor) => void;
}

const NotificationContext = createContext<NotificationContextType>(null!);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState({
    open: false,
    message: "",
    severity: "success" as AlertColor,
  });

  const notify = useCallback(
    (message: string, severity: AlertColor = "success") => {
      setState({ open: true, message, severity });
    },
    [],
  );

  const handleClose = (_: unknown, reason?: SnackbarCloseReason) => {
    if (reason === "clickaway") return;
    setState((prev) => ({ ...prev, open: false }));
  };

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => handleClose(null)}
          severity={state.severity}
          variant="filled"
          elevation={6}
          sx={{ maxWidth: 420, fontWeight: 500 }}
        >
          {state.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
}

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context)
    throw new Error("useNotification must be used within NotificationProvider");
  return context;
};
